import {
  Button,
  Dialog,
  DialogTitle,
  Paper,
  TableSortLabel,
} from "@mui/material";
import { Stack } from "@mui/system";
import { Link as RouterLink } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useSelector } from "react-redux";
import { useState, useMemo, useEffect } from "react";

interface KeywordsLinkedURLProps {
  open: boolean;
  handleClose: () => void;
  KeywordsTableHeadings: { [key: string]: string };
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

const KeywordsLinkedURLModal = ({
  open,
  handleClose,
  KeywordsTableHeadings,
}: KeywordsLinkedURLProps) => {
  const headCells = Object.entries(KeywordsTableHeadings);
  const { keywordsLinkedToURL } = useSelector(
    (state: any) => state.landingPageExperience
  );

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "cost",
    direction: "desc",
  });

  const [leavingUrl, setLeavingUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!leavingUrl) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(timer);
          window.location.href = leavingUrl; // Navigate in same tab
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [leavingUrl]);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedAndLimitedKeywords = useMemo(() => {
    if (!keywordsLinkedToURL || keywordsLinkedToURL.length === 0) return [];

    const sortableItems = [...keywordsLinkedToURL];

    sortableItems.sort((a, b) => {
      const aValue = a[sortConfig.key] || 0;
      const bValue = b[sortConfig.key] || 0;

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sortableItems.slice(0, 50);
  }, [keywordsLinkedToURL, sortConfig]);

  const handleClick = (row: any) => {
    const url = new URL(window.location.href);
    url.searchParams.set("keyword_internal_id", row.keyword_internal_id);
    setLeavingUrl(url.toString());
    setCountdown(3);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        PaperProps={{ sx: { width: "100%", maxWidth: "1400px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Other Keywords Linked To This Landing Page
          </DialogTitle>

          <TableContainer
            className="KeywordsTableWrap"
            component={Paper}
            sx={{ boxShadow: "none", maxHeight: 640 }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell key={index} align="left">
                      <TableSortLabel
                        active={sortConfig.key === key}
                        direction={
                          sortConfig.key === key ? sortConfig.direction : "asc"
                        }
                        onClick={() => handleSort(key)}
                      >
                        {value && typeof value === "string" ? value : ""}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAndLimitedKeywords.length > 0 ? (
                  sortedAndLimitedKeywords.map((row: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {headCells.map(([key], cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          align="left"
                          sx={{ py: "3px" }}
                        >
                          {key === "keyword_name" ? (
                            <RouterLink
                              to="#"
                              style={{
                                textDecoration: "underline",
                                color: "#9C27B0",
                                cursor: "pointer",
                              }}
                              onClick={() => handleClick(row)}
                            >
                              {row[key] || ""}
                            </RouterLink>
                          ) : key === "lp_exp_score_weight" ? (
                            (row[key] && row[key].toFixed(2)) || ""
                          ) : key === "quality_score" ? (
                            Math.round(row[key]) || ""
                          ) : key === "cost" ? (
                            row[key] && `${row[key].toLocaleString("en-US")}$`
                          ) : (
                            row[key] || ""
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={headCells.length}
                      align="center"
                      sx={{
                        py: 3,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
            <Button variant="contained" color="secondary" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Paper>
      </Dialog>

      {/* Stylish leaving alert */}
      <Dialog open={!!leavingUrl} onClose={() => setLeavingUrl(null)}>
        <Paper sx={{ padding: "24px", textAlign: "center" }}>
          <DialogTitle>You are leaving this page</DialogTitle>
          <p>
            Redirecting in <strong>{countdown}</strong> seconds...
          </p>
          {/* <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setLeavingUrl(null)}
            >
              Cancel
            </Button>
          </Stack> */}
        </Paper>
      </Dialog>
    </>
  );
};

export default KeywordsLinkedURLModal;
